/**
 * Cliente HTTP.
 *
 * Una sola función `pedir()` para toda la app. Se encarga de:
 *   • adjuntar el token,
 *   • cortar por timeout (una tablet con señal mala se queda colgada para
 *     siempre si nadie aborta el fetch),
 *   • convertir el `{ error: { codigo, mensaje } }` del backend en una
 *     excepción tipada que la capa de sync pueda inspeccionar por código.
 *
 * Lo que NO hace: reintentar. Los reintentos son decisión del outbox, que
 * sabe cuáles operaciones son seguras de repetir y cuáles no.
 */

import type { CuerpoError } from '../tipos/contrato';

const BASE = import.meta.env['VITE_API_URL'] ?? '';
const TIMEOUT_MS = 12_000;

const CLAVE_TOKEN = 'casanugget.token';

export const token = {
  leer: (): string | null => localStorage.getItem(CLAVE_TOKEN),
  guardar: (t: string) => localStorage.setItem(CLAVE_TOKEN, t),
  borrar: () => localStorage.removeItem(CLAVE_TOKEN),
};

/** Error con el código estable del backend, no con su mensaje. */
export class ErrorApi extends Error {
  constructor(
    readonly status: number,
    readonly codigo: string,
    mensaje: string,
    readonly detalle?: unknown,
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
  }

  /** Reintentar no va a cambiar el resultado: el servidor rechazó el dato. */
  get esDefinitivo(): boolean {
    return this.status >= 400 && this.status < 500 && this.status !== 408 && this.status !== 429;
  }
}

/** Sin señal, DNS caído, servidor apagado. Acá sí conviene reintentar. */
export class ErrorRed extends Error {
  constructor(mensaje = 'Sin conexión con el servidor') {
    super(mensaje);
    this.name = 'ErrorRed';
  }
}

interface Opciones {
  metodo?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  cuerpo?: unknown;
  senal?: AbortSignal;
}

export async function pedir<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const { metodo = 'GET', cuerpo, senal } = opciones;

  const control = new AbortController();
  const plazo = setTimeout(() => control.abort(), TIMEOUT_MS);
  senal?.addEventListener('abort', () => control.abort(), { once: true });

  const jwt = token.leer();

  let respuesta: Response;
  try {
    respuesta = await fetch(`${BASE}${ruta}`, {
      method: metodo,
      signal: control.signal,
      headers: {
        ...(cuerpo !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      },
      ...(cuerpo !== undefined ? { body: JSON.stringify(cuerpo) } : {}),
    });
  } catch {
    throw new ErrorRed();
  } finally {
    clearTimeout(plazo);
  }

  if (respuesta.status === 204) return undefined as T;

  const texto = await respuesta.text();
  const datos: unknown = texto ? JSON.parse(texto) : null;

  if (!respuesta.ok) {
    const cuerpoError = datos as CuerpoError | null;
    throw new ErrorApi(
      respuesta.status,
      cuerpoError?.error?.codigo ?? 'DESCONOCIDO',
      cuerpoError?.error?.mensaje ?? `Error ${respuesta.status}`,
      cuerpoError?.error?.detalle,
    );
  }

  return datos as T;
}
